/**
 * TouchZones component
 *
 * Visual touch targets for mobile/tablet gameplay.
 * Shows 6 lanes at the bottom of the game area with press feedback.
 */

import React from 'react';

interface TouchZonesProps {
  activeLanes: Set<number>;
  isVisible: boolean;
  laneCount?: number;
  onLanePress?: (lane: number) => void;
}

// Warm color palette matching the game
const LANE_COLORS = [
  { main: '#f5a623', glow: 'rgba(245, 166, 35, 0.4)' },   // amber
  { main: '#e8b86d', glow: 'rgba(232, 184, 109, 0.4)' },   // gold
  { main: '#e07a5f', glow: 'rgba(224, 122, 95, 0.4)' },    // coral
  { main: '#f5a623', glow: 'rgba(245, 166, 35, 0.4)' },    // amber
  { main: '#e8b86d', glow: 'rgba(232, 184, 109, 0.4)' },   // gold
  { main: '#e07a5f', glow: 'rgba(224, 122, 95, 0.4)' },    // coral
];

const LANE_LABELS = ['S', 'D', 'F', 'J', 'K', 'L'];

export const TouchZones: React.FC<TouchZonesProps> = ({
  activeLanes,
  isVisible,
  laneCount = 6,
  onLanePress,
}) => {
  if (!isVisible) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 h-24 flex">
      {Array.from({ length: laneCount }, (_, lane) => {
        const isActive = activeLanes.has(lane);
        const colors = LANE_COLORS[lane];

        return (
          <button
            type="button"
            key={lane}
            onClick={() => onLanePress?.(lane)}
            className="flex-1 relative flex flex-col items-center justify-center transition-all duration-75"
            style={{
              background: isActive
                ? `linear-gradient(to top, ${colors.glow} 0%, transparent 100%)`
                : 'transparent',
              transform: isActive ? 'scale(0.95)' : 'scale(1)',
            }}
          >
            {/* Touch zone indicator */}
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-100"
              style={{
                backgroundColor: isActive ? colors.main : 'rgba(20, 20, 20, 0.8)',
                border: `3px solid ${isActive ? colors.main : colors.main + '60'}`,
                color: isActive ? '#0a0a0b' : colors.main,
                boxShadow: isActive
                  ? `0 0 20px ${colors.glow}, 0 0 40px ${colors.glow}`
                  : 'none',
                transform: isActive ? 'scale(0.9)' : 'scale(1)',
              }}
            >
              {LANE_LABELS[lane]}
            </div>

            {/* Lane indicator line */}
            <div
              className="absolute top-0 w-[2px] h-full opacity-20"
              style={{
                backgroundColor: colors.main,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />
          </button>
        );
      })}
    </div>
  );
};

export default TouchZones;
